import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  TextField,
  Rating,
  Button,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
} from "@mui/material";
import { StarRate, Star, CheckCircle } from "@mui/icons-material";
import toast from "react-hot-toast";

const API = process.env.REACT_APP_API_URL;

const StudentReview = () => {
  const [review, setReview] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", rating: 0 });
  const [loading, setLoading] = useState(false);

  const fetchReview = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}reviews/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.length > 0) {
        setReview(res.data[0]);
      }
    } catch (err) {
      toast.error("Failed to fetch review");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.rating === 0) {
      toast.error("Please select a rating");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}reviews/`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      toast.success("✅ Review submitted successfully!");
      fetchReview();
    } catch (err) {
      toast.error("❌ Failed to submit review");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReview();
  }, []);

  if (review) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 md:p-4 lg:p-6">
        <div className="max-w-4xl mx-auto space-y-4 md:space-y-6">
          {/* Header Section */}
          <div className="bg-white rounded-xl shadow-lg p-3 md:p-6">
            <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
              <div className="p-2 md:p-3 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl">
                <StarRate className="text-white text-lg md:text-2xl" />
              </div>
              <div>
                <h2 className="text-lg md:text-2xl font-bold text-gray-800">
                  Your Submitted Review
                </h2>
                <p className="text-gray-600 text-xs md:text-sm">
                  View your feedback and rating
                </p>
              </div>
            </div>

            {/* Review Card */}
            <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200">
              <CardContent className="p-3 md:p-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-0 mb-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="bg-gradient-to-r from-yellow-500 to-orange-600">
                      <Star className="text-white" />
                    </Avatar>
                    <div>
                      <Typography
                        variant="h6"
                        className="font-bold text-gray-800 text-sm md:text-base"
                      >
                        {review.title}
                      </Typography>
                      <div className="flex items-center gap-2 mt-1">
                        <Rating value={review.rating} readOnly size="small" />
                        <span className="text-xs md:text-sm text-gray-600">
                          ({review.rating} stars)
                        </span>
                      </div>
                    </div>
                  </div>
                  <Chip
                    icon={<CheckCircle />}
                    label="Submitted"
                    color="success"
                    size="small"
                    sx={{ borderRadius: "12px" }}
                  />
                </div>

                <Divider className="my-3 md:my-4" />

                <div className="space-y-3 md:space-y-4">
                  <div>
                    <Typography
                      variant="subtitle2"
                      className="text-gray-600 mb-1"
                    >
                      Description
                    </Typography>
                    <Typography
                      variant="body1"
                      className="text-gray-800 bg-white p-2 md:p-3 rounded-lg border text-sm md:text-base"
                    >
                      {review.description}
                    </Typography>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1 md:gap-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs md:text-sm text-gray-500">
                        Submitted on:
                      </span>
                      <span className="text-xs md:text-sm font-medium text-gray-700">
                        {new Date(review.submitted_on).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="text-xs md:text-sm text-gray-500">
                      {new Date(review.submitted_on).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 md:p-4 lg:p-6">
      <div className="max-w-2xl mx-auto space-y-4 md:space-y-6">
        {/* Header Section */}
        <div className="bg-white rounded-xl shadow-lg p-3 md:p-6">
          <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
            <div className="p-2 md:p-3 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl">
              <StarRate className="text-white text-lg md:text-2xl" />
            </div>
            <div>
              <h2 className="text-lg md:text-2xl font-bold text-gray-800">
                Submit Your Review
              </h2>
              <p className="text-gray-600 text-xs md:text-sm">
                Share your experience and feedback
              </p>
            </div>
          </div>

          {/* Review Form */}
          <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200">
            <CardContent className="p-3 md:p-6">
              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                <div>
                  <Typography
                    variant="subtitle2"
                    className="text-gray-700 mb-2 font-medium"
                    sx={{ marginBottom: "10px" }}
                  >
                    Review Title
                  </Typography>
                  <TextField
                    fullWidth
                    label="Enter a title for your review"
                    name="title"
                    variant="outlined"
                    value={form.title}
                    onChange={(e) =>
                      setForm({ ...form, title: e.target.value })
                    }
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                    }}
                  />
                </div>

                <div>
                  <Typography
                    variant="subtitle2"
                    className="text-gray-700 mb-2 font-medium"
                    sx={{ marginBottom: "10px" }}
                  >
                    Description
                  </Typography>
                  <TextField
                    fullWidth
                    label="Share your detailed experience"
                    name="description"
                    multiline
                    rows={4}
                    variant="outlined"
                    value={form.description}
                    onChange={(e) =>
                      setForm({ ...form, description: e.target.value })
                    }
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                    }}
                  />
                </div>

                <div>
                  <Typography
                    variant="subtitle2"
                    className="text-gray-700 mb-2 font-medium"
                  >
                    Rating
                  </Typography>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                    <Rating
                      name="rating"
                      value={form.rating}
                      onChange={(e, newValue) =>
                        setForm({ ...form, rating: newValue })
                      }
                      size="large"
                      sx={{
                        "& .MuiRating-iconFilled": {
                          color: "#f59e0b",
                        },
                        "& .MuiRating-iconHover": {
                          color: "#d97706",
                        },
                      }}
                    />
                    <Typography
                      variant="body2"
                      className="text-gray-600 text-sm"
                    >
                      {form.rating > 0
                        ? `${form.rating} star${form.rating > 1 ? "s" : ""}`
                        : "Select rating"}
                    </Typography>
                  </div>
                </div>

                <Divider />

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || form.rating === 0}
                    startIcon={<StarRate />}
                    sx={{
                      borderRadius: "12px",
                      background:
                        "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                      },
                      "&:disabled": {
                        background: "#d1d5db",
                        color: "#9ca3af",
                      },
                      py: 1.5,
                      px: 3,
                    }}
                  >
                    {loading ? "Submitting..." : "Submit Review"}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() =>
                      setForm({ title: "", description: "", rating: 0 })
                    }
                    sx={{
                      borderRadius: "12px",
                      borderColor: "#6b7280",
                      color: "#6b7280",
                      "&:hover": {
                        borderColor: "#4b5563",
                        backgroundColor: "rgba(107, 114, 128, 0.04)",
                      },
                    }}
                  >
                    Clear Form
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Tips Section */}
          <div className="bg-white rounded-xl shadow-lg p-3 md:p-6">
            <div className="flex items-start gap-2 md:gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <span className="text-blue-600 text-lg md:text-xl">💡</span>
              </div>
              <div>
                <Typography
                  variant="h6"
                  className="font-semibold text-gray-800 mb-2 text-base md:text-lg"
                >
                  Review Tips
                </Typography>
                <div className="space-y-1 md:space-y-2 text-xs md:text-sm text-gray-600">
                  <p>• Be specific about your experience with our services</p>
                  <p>
                    • Mention both positive aspects and areas for improvement
                  </p>
                  <p>
                    • Your feedback helps us provide better service to all
                    students
                  </p>
                  <p>• Reviews are moderated and may take time to appear</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentReview;
